import type RobustWebSocket from "robust-websocket";
import { Observable, Subject } from "rxjs";
import { logD, logE } from "@blacknut/logging/dist";

export type NotificationMessage = {
  kind: string;
  payload: unknown;
};

const TAG = "NotificationService";

class NotificationService {
  private _endpoint: string | undefined;
  public get endpoint(): string | undefined {
    return this._endpoint;
  }
  public set endpoint(v: string | undefined) {
    this._endpoint = v;
  }

  protected ws: RobustWebSocket | undefined;

  private _onMessageSubject = new Subject<NotificationMessage>();

  protected _onReadySubject = new Subject<void>();

  private shouldReconnect = true;

  public onMessage(): Observable<NotificationMessage> {
    return this._onMessageSubject.asObservable();
  }

  protected _connect(kind: string, responseKind: string, payload?: unknown) {
    logD(TAG, "Connecting");
    if (!this.endpoint) {
      throw new Error("End point not defined");
    }
    if (this.ws) {
      logE(TAG, "Websocket is already opened");
      return;
    }
    this.shouldReconnect = true;
    import("robust-websocket").then(({ default: RobustWebSocket }) => {
      this.ws = new RobustWebSocket(this.endpoint!, undefined, {
        shouldReconnect: (event, ws) => {
          logD(
            TAG,
            "in shouldReconnect: %o, shouldReconnect: %o (%o)",
            event,
            this.shouldReconnect,
            ws.attempts,
          );
          if (!this.shouldReconnect) return false;
          if (event.code === 1008 || event.code === 1011) return false;
          return [0, 1000, 3000, 5000, 10000][ws.attempts];
        },
      });
      this.ws.addEventListener("open", () => {
        logD(TAG, "Open OK");
        setTimeout(() => {
          try {
            this.sendSync(kind, payload, responseKind).subscribe({
              next: () => {
                this._onReadySubject.next();
                this._onReadySubject.complete();
              },
              error: (err) => {
                logE(TAG, "Caught error on connect kind=%o error=%o", kind, err);
                this._onReadySubject.error(err);
                this._onReadySubject.complete();
              },
            });
          } catch (err) {
            logE(TAG, "Caught error on connect kind=%o error=%o", kind, err);
          }
        }, 100);
      });
      this.ws.addEventListener("message", (event) => {
        try {
          this._onMessageSubject.next(JSON.parse(event.data));
        } catch (e) {
          logE(TAG, "Caught error parsing JSON", event.data);
        }
      });
    });
  }

  public connected() {
    return !!this.ws && this.ws.readyState === WebSocket.OPEN;
  }

  public send(kind: string, payload?: unknown) {
    if (this.ws) {
      logD(TAG, "Sending to ws %o", { kind, payload });
      this.ws.send(JSON.stringify({ kind, payload }));
    } else {
      throw new Error("Socked closed");
    }
  }

  public sendSync(kind: string, payload: unknown, responseKind: string) {
    if (this.ws) {
      const res = new Subject<unknown>();
      const subscription = this.onMessage().subscribe({
        next: (m) => {
          if (m.kind === responseKind) {
            subscription.unsubscribe();
            res.next(m.payload);
          }
        },
        error: (err) => {
          logE(TAG, "Caught error on ws", err);
          subscription.unsubscribe();
          res.error(err);
        },
      });

      try {
        this.send(kind, payload);
      } catch (e) {
        logE(TAG, "Caught error on send: %o (state is %o)", e, this.ws.readyState);
      }

      return res.asObservable();
    } else {
      throw new Error("Socked closed");
    }
  }

  public disconnect() {
    logD(TAG, "Disconnect");
    this.shouldReconnect = false;

    this.ws?.close();
    this.ws = undefined;
  }
}

export { NotificationService };
